import React, { useState } from 'react';
import { 
  Calendar, 
  MapPin, 
  Clock, 
  DollarSign, 
  Users, 
  ExternalLink,
  ChevronRight,
  Star
} from 'lucide-react';

/**
 * Local Events Card Component
 * 
 * Displays community events and activity recommendations
 * with relevance scoring and family-appropriate filtering
 */
function LocalEventsCard({ 
  localEvents = [], 
  recommendations = [], 
  onViewEvent,
  onViewAllEvents 
}) {
  const [activeTab, setActiveTab] = useState('events');

  const formatCost = (cost) => {
    if (!cost || cost === 'free') return 'Free';
    if (cost === 'varies') return 'Varies';
    return cost;
  };

  const formatAgeGroups = (ageGroups) => {
    if (!ageGroups || ageGroups.includes('all')) return 'All ages';
    return ageGroups.map(group => 
      group.charAt(0).toUpperCase() + group.slice(1)
    ).join(', ');
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.8) return 'text-emerald-600';
    if (confidence >= 0.6) return 'text-amber-600';
    return 'text-slate-600';
  };

  const renderEvent = (event) => (
    <div 
      key={event.id}
      className="local-event-item"
      onClick={() => onViewEvent && onViewEvent(event)}
    >
      <div className="event-header">
        <div className="event-title">{event.title}</div>
        <div className="event-meta">
          {event.time && (
            <span className="meta-item">
              <Clock size={12} />
              {event.time}
            </span>
          )}
          {event.location && (
            <span className="meta-item">
              <MapPin size={12} />
              {event.location}
            </span>
          )}
        </div>
      </div>
      
      <div className="event-description">{event.description}</div>
      
      <div className="event-details">
        <div className="detail-row">
          <span className="detail-item">
            <DollarSign size={12} />
            {formatCost(event.cost)}
          </span>
          <span className="detail-item">
            <Users size={12} />
            {formatAgeGroups(event.ageGroups)}
          </span>
          {event.duration && (
            <span className="detail-item">
              <Clock size={12} />
              {event.duration}min
            </span>
          )}
        </div>
      </div>
    </div>
  );

  const renderRecommendation = (rec) => (
    <div 
      key={rec.title}
      className="recommendation-item"
    >
      <div className="rec-header">
        <div className="rec-title">{rec.title}</div>
        {rec.confidence && (
          <div className={`confidence-score ${getConfidenceColor(rec.confidence)}`}>
            <Star size={12} fill="currentColor" />
            {Math.round(rec.confidence * 100)}%
          </div>
        )}
      </div>
      
      <div className="rec-description">{rec.description}</div>
      
      <div className="rec-details">
        <span className="rec-category">{rec.category}</span>
        {rec.duration && (
          <span className="rec-duration">{rec.duration}min</span>
        )}
        {rec.weatherDependent && (
          <span className="weather-dependent">Weather-based</span>
        )}
      </div>
    </div>
  );

  if (!localEvents.length && !recommendations.length) {
    return (
      <div className="local-events-card empty">
        <div className="card-header">
          <Calendar size={20} />
          <div className="header-text">
            <div className="card-title">Local Events</div>
            <div className="card-subtitle">No events found</div>
          </div>
        </div>
        <div className="empty-state">
          <p>Check back later for community events and activity recommendations.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="local-events-card">
      <div className="card-header">
        <Calendar size={20} />
        <div className="header-text">
          <div className="card-title">Local Events</div>
          <div className="card-subtitle">
            {activeTab === 'events' 
              ? `${localEvents.length} upcoming events`
              : `${recommendations.length} recommendations`
            }
          </div>
        </div>
        {onViewAllEvents && (
          <button 
            className="view-all-btn"
            onClick={onViewAllEvents}
          >
            <ExternalLink size={16} />
          </button>
        )}
      </div>

      <div className="tab-selector">
        <button 
          className={`tab-btn ${activeTab === 'events' ? 'active' : ''}`}
          onClick={() => setActiveTab('events')}
        >
          Events ({localEvents.length})
        </button>
        <button 
          className={`tab-btn ${activeTab === 'recommendations' ? 'active' : ''}`}
          onClick={() => setActiveTab('recommendations')}
        >
          For You ({recommendations.length})
        </button>
      </div>

      <div className="card-content">
        {activeTab === 'events' ? (
          <div className="events-list">
            {localEvents.slice(0, 3).map(renderEvent)}
            {localEvents.length > 3 && (
              <button 
                className="view-more-btn"
                onClick={onViewAllEvents}
              >
                View {localEvents.length - 3} more events
                <ChevronRight size={14} />
              </button>
            )}
          </div>
        ) : (
          <div className="recommendations-list">
            {recommendations.slice(0, 4).map(renderRecommendation)}
          </div>
        )}
      </div>
    </div>
  );
}

export default LocalEventsCard;